import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { adminContent, images as imageApi } from 'services/api';
import type { AdminInspireDeck } from 'services/api/adminContentClient';
import type {
  CohortRole,
  CohortSummary,
  ContentDefaultsResponse,
  MediaAsset,
  OrganizationSummary,
  SignupCodeSummary,
} from 'types';
import ContentAssignmentEditor, {
  type DraftCourseAssignment,
  type DraftFlowAssignment,
} from 'components/admin/ContentAssignmentEditor';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Slider } from 'components/ui/slider';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Textarea } from 'components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { getCohortRoleLabel, getInviteeBucketLabel, lowerLabel } from 'utils/cohortRoleLabels';

const ALLOWED_ICON_MIME_TYPES = new Set(['image/png', 'image/jpeg']);
const ALLOWED_ICON_FILENAME = /\.(png|jpe?g)$/i;
const EMAIL_DOMAIN_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

type OrganizationDraft = {
  id?: string;
  name: string;
  org_id: string;
  icon: string | null;
  icon_media: MediaAsset | null;
  allowed_email_domains_text: string;
  is_active: boolean;
};

type SignupCodeDraft = {
  name: string;
  code: string;
  cohort_id: string;
  role: CohortRole;
  expires_at: string;
};

const EMPTY_ORGANIZATION: OrganizationDraft = {
  name: '',
  org_id: '',
  icon: null,
  icon_media: null,
  allowed_email_domains_text: '',
  is_active: true,
};

const EMPTY_CONTENT_DEFAULTS: ContentDefaultsResponse = {
  course_assignments: [],
  flow_assignments: [],
  inspire_deck_assignments: [],
  inspire_program_name: '',
  inspire_program_logo_id: null,
  override_wisefriend_inspire_defaults: false,
};

const toLocalDateTimeInputValue = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const DEFAULT_SIGNUP_CODE_DRAFT = (): SignupCodeDraft => {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);
  return {
    name: '',
    code: '',
    cohort_id: '',
    role: 'mentee',
    expires_at: toLocalDateTimeInputValue(expiresAt),
  };
};

const normalizeList = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload?.results && Array.isArray(payload.results)) return payload.results as T[];
  return [];
};

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (data && typeof data === 'object') {
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim()) return value;
      if (Array.isArray(value)) {
        const firstString = value.find((item): item is string => typeof item === 'string' && item.trim().length > 0);
        if (firstString) return firstString;
      }
    }
  }
  return fallback;
};

const extractErrorText = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = extractErrorText(entry);
      if (message) return message;
    }
  }
  if (value && typeof value === 'object') {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      const message = extractErrorText(entry);
      if (message) return message;
    }
  }
  return null;
};

const findApiFieldError = (value: unknown, fieldName: string): string | null => {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = findApiFieldError(entry, fieldName);
      if (message) return message;
    }
    return null;
  }
  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(record, fieldName)) {
    const message = extractErrorText(record[fieldName]);
    if (message) return message;
  }
  for (const entry of Object.values(record)) {
    const message = findApiFieldError(entry, fieldName);
    if (message) return message;
  }
  return null;
};

const extractApiFieldErrorMessage = (error: any, fieldNames: string[]): string | null => {
  const data = error?.response?.data;
  for (const fieldName of fieldNames) {
    const message = findApiFieldError(data, fieldName);
    if (message) return message;
  }
  return null;
};

const organizationToDraft = (organization: OrganizationSummary): OrganizationDraft => ({
  id: organization.id,
  name: organization.name ?? '',
  org_id: organization.org_id ?? '',
  icon: organization.icon ?? null,
  icon_media: organization.icon_media ?? null,
  allowed_email_domains_text: (organization.allowed_email_domains ?? []).join(', '),
  is_active: organization.is_active !== false,
});

const parseDomainList = (rawValue: string): string[] =>
  Array.from(
    new Set(
      rawValue
        .split(/[\n,]/)
        .map((item) => item.trim().toLowerCase())
        .filter((item) => item.length > 0),
    ),
  );

const getDomainValidationError = (domain: string): string | null => {
  if (/\s/.test(domain)) return 'Domains cannot contain whitespace.';
  if (domain.includes('*')) {
    return 'Wildcard domains are not supported. Enter the parent domain instead.';
  }
  if (domain.length > 253) return 'Domains must be 253 characters or fewer.';
  const labels = domain.split('.');
  if (labels.length < 2 || labels.some((label) => !label)) {
    return 'Enter complete domains with no missing labels, such as example.org.';
  }
  if (labels.some((label) => !EMAIL_DOMAIN_LABEL_PATTERN.test(label))) {
    return 'Domain labels may contain only letters, numbers, and internal hyphens.';
  }
  if (!/[a-z]/.test(labels[labels.length - 1])) {
    return 'The final domain label must contain a letter.';
  }
  return null;
};

const getDomainListValidationError = (rawValue: string): string | null => {
  const invalidDomain = parseDomainList(rawValue).find((domain) => getDomainValidationError(domain));
  return invalidDomain
    ? `${invalidDomain}: ${getDomainValidationError(invalidDomain)}`
    : null;
};

const normalizeSignupCode = (value: string): string => value.toUpperCase();

const sanitizeOrganizationNameForPath = (value: string): string => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
  return slug || 'organization';
};

const isAllowedIconFile = (file: File): boolean => {
  const mimeType = (file.type || '').toLowerCase();
  if (ALLOWED_ICON_MIME_TYPES.has(mimeType)) return true;
  if (!mimeType || mimeType === 'application/octet-stream') {
    return ALLOWED_ICON_FILENAME.test(file.name || '');
  }
  return false;
};

const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedBlob = async (imageSrc: string, pixelCrop: Area, mimeType: string) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const size = Math.min(800, Math.max(pixelCrop.width, pixelCrop.height));
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not prepare crop canvas');
  }
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, size, size);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to crop image'));
      },
      mimeType || 'image/jpeg',
      0.9,
    );
  });
};

const Organizations: React.FC = () => {
  const { addToast } = useToast();
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [cohorts, setCohorts] = useState<CohortSummary[]>([]);
  const [signupCodes, setSignupCodes] = useState<SignupCodeSummary[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [organizationDraft, setOrganizationDraft] = useState<OrganizationDraft>(EMPTY_ORGANIZATION);
  const [organizationDomainError, setOrganizationDomainError] = useState<string | null>(null);
  const [signupCodeDraft, setSignupCodeDraft] = useState<SignupCodeDraft>(DEFAULT_SIGNUP_CODE_DRAFT());
  const [contentDefaults, setContentDefaults] = useState<ContentDefaultsResponse>(EMPTY_CONTENT_DEFAULTS);
  const [draftDefaultCourses, setDraftDefaultCourses] = useState<DraftCourseAssignment[]>([]);
  const [draftDefaultFlows, setDraftDefaultFlows] = useState<DraftFlowAssignment[]>([]);
  const [availableInspireDecks, setAvailableInspireDecks] = useState<AdminInspireDeck[]>([]);
  const [draftInspireDeckIds, setDraftInspireDeckIds] = useState<string[]>([]);
  const [draftOverrideWiseFriendInspireDefaults, setDraftOverrideWiseFriendInspireDefaults] = useState(false);
  const [draftInspireProgramName, setDraftInspireProgramName] = useState('');
  const [draftInspireProgramLogoId, setDraftInspireProgramLogoId] = useState('');
  const [draftInspireProgramLogoUrl, setDraftInspireProgramLogoUrl] = useState('');
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [isLoadingSignupCodes, setIsLoadingSignupCodes] = useState(false);
  const [isLoadingContentDefaults, setIsLoadingContentDefaults] = useState(false);
  const [isSavingOrganization, setIsSavingOrganization] = useState(false);
  const [isSavingSignupCode, setIsSavingSignupCode] = useState(false);
  const [isSavingDefaultCourses, setIsSavingDefaultCourses] = useState(false);
  const [isSavingDefaultFlows, setIsSavingDefaultFlows] = useState(false);
  const [isSavingInspireDefaults, setIsSavingInspireDefaults] = useState(false);
  const [isUploadingInspireLogo, setIsUploadingInspireLogo] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const [isCroppingIcon, setIsCroppingIcon] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isRevealDialogOpen, setIsRevealDialogOpen] = useState(false);
  const [revealedSignupCode, setRevealedSignupCode] = useState('');
  const iconInputRef = useRef<HTMLInputElement | null>(null);
  const inspireLogoInputRef = useRef<HTMLInputElement | null>(null);
  const organizationDomainValidationActiveRef = useRef(false);

  const loadOrganizations = useCallback(async () => {
    setIsLoadingOrganizations(true);
    try {
      const response = await adminContent.organizations.list();
      setOrganizations(normalizeList<OrganizationSummary>(response.data));
    } catch (error) {
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to load organizations.'),
        variant: 'destructive',
      });
    } finally {
      setIsLoadingOrganizations(false);
    }
  }, [addToast]);

  const loadCohorts = useCallback(async () => {
    try {
      const response = await adminContent.cohorts.list();
      setCohorts(normalizeList<CohortSummary>(response.data));
    } catch (error) {
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to load cohorts.'),
        variant: 'destructive',
      });
    }
  }, [addToast]);

  const loadSignupCodes = useCallback(
    async (organizationId: string) => {
      setIsLoadingSignupCodes(true);
      try {
        const response = await adminContent.signupCodes.list({ organization_id: organizationId });
        setSignupCodes(normalizeList<SignupCodeSummary>(response.data));
      } catch (error) {
        addToast({
          title: 'Error',
          description: extractApiErrorMessage(error, 'Unable to load signup codes.'),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingSignupCodes(false);
      }
    },
    [addToast],
  );

  const applyContentDefaultsResponse = useCallback((data: ContentDefaultsResponse) => {
    setContentDefaults(data);
    setDraftDefaultCourses(
      (data.course_assignments ?? []).map((assignment) => ({
        course_id: assignment.course.id,
        name: assignment.course.name,
      })),
    );
    setDraftDefaultFlows(
      (data.flow_assignments ?? []).map((assignment) => ({
        flow_id: assignment.flow.id,
        name: assignment.flow.name,
      })),
    );
    setDraftInspireDeckIds((data.inspire_deck_assignments ?? []).map((assignment) => assignment.deck.id));
    setDraftOverrideWiseFriendInspireDefaults(Boolean(data.override_wisefriend_inspire_defaults));
    setDraftInspireProgramName(data.inspire_program_name ?? '');
    setDraftInspireProgramLogoId(data.inspire_program_logo_id ?? '');
    setDraftInspireProgramLogoUrl(data.inspire_program_logo_url ?? '');
  }, []);

  const loadOrganizationContentDefaults = useCallback(
    async (organizationId: string) => {
      setIsLoadingContentDefaults(true);
      try {
        const response = await adminContent.organizations.getContentDefaults(organizationId);
        applyContentDefaultsResponse(response.data as ContentDefaultsResponse);
      } catch (error) {
        addToast({
          title: 'Error',
          description: extractApiErrorMessage(error, 'Unable to load organization content defaults.'),
          variant: 'destructive',
        });
        applyContentDefaultsResponse(EMPTY_CONTENT_DEFAULTS);
      } finally {
        setIsLoadingContentDefaults(false);
      }
    },
    [addToast, applyContentDefaultsResponse],
  );

  const loadInspireDeckOptions = useCallback(async (organizationId: string) => {
    try {
      const [globalResponse, organizationResponse] = await Promise.all([
        adminContent.inspireDecks.list('global'),
        adminContent.inspireDecks.list(organizationId),
      ]);
      const combined = [
        ...normalizeList<AdminInspireDeck>(globalResponse.data),
        ...normalizeList<AdminInspireDeck>(organizationResponse.data),
      ];
      setAvailableInspireDecks(combined.filter((deck, index, all) => all.findIndex((item) => item.id === deck.id) === index));
    } catch {
      setAvailableInspireDecks([]);
    }
  }, []);

  const moveDraftInspireDeck = useCallback((deckId: string, direction: -1 | 1) => {
    setDraftInspireDeckIds((current) => {
      const index = current.indexOf(deckId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  }, []);

  const handleInspireLogoFileSelected = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!isAllowedIconFile(file)) {
      addToast({ title: 'Invalid file type', description: 'Only PNG and JPG files are supported for Inspire program logos.', variant: 'destructive' });
      return;
    }
    setIsUploadingInspireLogo(true);
    try {
      const baseName = organizationDraft.name.trim() || organizationDraft.org_id.trim() || 'organization';
      const uploaded = await imageApi.uploadWithMetadata(file, {
        visibility: 'public',
        folder: `organizations/${sanitizeOrganizationNameForPath(baseName)}/inspire/`,
      });
      if (!uploaded.assetId) throw new Error('Upload succeeded but media asset was not returned.');
      setDraftInspireProgramLogoId(uploaded.assetId);
      try {
        setDraftInspireProgramLogoUrl(await imageApi.getSignedUrl(uploaded.key));
      } catch {
        setDraftInspireProgramLogoUrl('');
      }
      addToast({ title: 'Logo uploaded', description: 'Save Inspire defaults to use this logo.' });
    } catch (error) {
      addToast({ title: 'Upload failed', description: extractApiErrorMessage(error, 'Unable to upload the Inspire program logo.'), variant: 'destructive' });
    } finally {
      setIsUploadingInspireLogo(false);
    }
  }, [addToast, organizationDraft.name, organizationDraft.org_id]);

  useEffect(() => {
    void loadOrganizations();
    void loadCohorts();
  }, [loadCohorts, loadOrganizations]);

  useEffect(() => {
    if (!selectedOrganizationId) {
      setSignupCodes([]);
      applyContentDefaultsResponse(EMPTY_CONTENT_DEFAULTS);
      return;
    }
    void loadSignupCodes(selectedOrganizationId);
    void loadOrganizationContentDefaults(selectedOrganizationId);
    void loadInspireDeckOptions(selectedOrganizationId);
  }, [applyContentDefaultsResponse, loadInspireDeckOptions, loadOrganizationContentDefaults, loadSignupCodes, selectedOrganizationId]);

  const filteredOrganizations = useMemo(
    () => [...organizations].sort((a, b) => a.name.localeCompare(b.name)),
    [organizations],
  );

  const inheritedWiseFriendDecks = useMemo(
    () => availableInspireDecks.filter(
      (deck) => !deck.owner_organization && deck.status === 'published',
    ),
    [availableInspireDecks],
  );

  const handleInspireCatalogModeChange = useCallback((mode: string) => {
    const useOrganizationCatalog = mode === 'organization';
    if (
      useOrganizationCatalog
      && !draftOverrideWiseFriendInspireDefaults
      && draftInspireDeckIds.length === 0
    ) {
      setDraftInspireDeckIds(inheritedWiseFriendDecks.map((deck) => deck.id));
    }
    setDraftOverrideWiseFriendInspireDefaults(useOrganizationCatalog);
  }, [draftInspireDeckIds.length, draftOverrideWiseFriendInspireDefaults, inheritedWiseFriendDecks]);

  const organizationCohorts = useMemo(
    () => cohorts.filter((cohort) => cohort.organization?.id === selectedOrganizationId),
    [cohorts, selectedOrganizationId],
  );
  const signupCodeCohort = useMemo(
    () => organizationCohorts.find((cohort) => cohort.id === signupCodeDraft.cohort_id) ?? null,
    [organizationCohorts, signupCodeDraft.cohort_id],
  );

  const defaultCoursesDirty = useMemo(() => {
    const serverIds = (contentDefaults.course_assignments ?? []).map((assignment) => assignment.course.id);
    const draftIds = draftDefaultCourses.map((assignment) => assignment.course_id);
    return JSON.stringify(serverIds) !== JSON.stringify(draftIds);
  }, [contentDefaults.course_assignments, draftDefaultCourses]);

  const defaultFlowsDirty = useMemo(() => {
    const serverIds = (contentDefaults.flow_assignments ?? []).map((assignment) => assignment.flow.id);
    const draftIds = draftDefaultFlows.map((assignment) => assignment.flow_id);
    if (serverIds.length !== draftIds.length) return true;
    const draftSet = new Set(draftIds);
    return serverIds.some((id) => !draftSet.has(id));
  }, [contentDefaults.flow_assignments, draftDefaultFlows]);

  const resetCropState = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropImageSrc(null);
    setPendingIconFile(null);
    setIsCroppingIcon(false);
  }, []);

  const handleSelectOrganization = useCallback(
    (organization: OrganizationSummary | null) => {
      organizationDomainValidationActiveRef.current = false;
      setOrganizationDomainError(null);
      if (!organization) {
        setSelectedOrganizationId(null);
        setOrganizationDraft(EMPTY_ORGANIZATION);
        setSignupCodeDraft(DEFAULT_SIGNUP_CODE_DRAFT());
        setSignupCodes([]);
        applyContentDefaultsResponse(EMPTY_CONTENT_DEFAULTS);
        return;
      }
      setSelectedOrganizationId(organization.id);
      setOrganizationDraft(organizationToDraft(organization));
      setSignupCodeDraft(DEFAULT_SIGNUP_CODE_DRAFT());
    },
    [applyContentDefaultsResponse],
  );

  const handleOrganizationDraftChange = useCallback(
    (field: keyof OrganizationDraft, value: string | boolean | null) => {
      setOrganizationDraft((prev) => ({ ...prev, [field]: value }));
      if (field === 'allowed_email_domains_text') {
        if (organizationDomainValidationActiveRef.current) {
          setOrganizationDomainError(
            getDomainListValidationError(typeof value === 'string' ? value : ''),
          );
        }
      }
    },
    [],
  );

  const handleIconUpload = useCallback(
    async (file: File) => {
      setIsUploadingIcon(true);
      try {
        const baseName = organizationDraft.name.trim() || organizationDraft.org_id.trim() || 'organization';
        const folder = `organizations/${sanitizeOrganizationNameForPath(baseName)}/icon/`;
        const uploaded = await imageApi.uploadWithMetadata(file, {
          visibility: 'public',
          folder,
        });
        const assetId = uploaded.assetId;
        if (!assetId) {
          throw new Error('Upload succeeded but media asset was not returned.');
        }

        let iconUrl: string | null = null;
        try {
          iconUrl = await imageApi.getSignedUrl(uploaded.key);
        } catch (error) {
          iconUrl = null;
        }

        setOrganizationDraft((prev) => ({
          ...prev,
          icon: assetId,
          icon_media: {
            id: assetId,
            storage_key: uploaded.key,
            media_type: 'image',
            mime_type: uploaded.mimeType ?? file.type ?? null,
            is_active: true,
            url: iconUrl,
          },
        }));
        addToast({ title: 'Icon uploaded', description: 'Save organization to persist this icon.' });
      } catch (error) {
        addToast({
          title: 'Upload failed',
          description: extractApiErrorMessage(error, 'Unable to upload organization icon.'),
          variant: 'destructive',
        });
      } finally {
        setIsUploadingIcon(false);
      }
    },
    [addToast, organizationDraft.name, organizationDraft.org_id],
  );

  const prepareIconCrop = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImageSrc(reader.result as string);
      setPendingIconFile(file);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleIconFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!isAllowedIconFile(file)) {
        addToast({
          title: 'Invalid file type',
          description: 'Only PNG and JPG files are supported for organization icons.',
          variant: 'destructive',
        });
        return;
      }

      prepareIconCrop(file);
    },
    [addToast, prepareIconCrop],
  );

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirmIconCrop = useCallback(async () => {
    if (!cropImageSrc || !pendingIconFile || !croppedAreaPixels) {
      setIsCropDialogOpen(false);
      resetCropState();
      return;
    }
    setIsCroppingIcon(true);
    try {
      const mimeType = pendingIconFile.type || 'image/jpeg';
      const blob = await getCroppedBlob(cropImageSrc, croppedAreaPixels, mimeType);
      const croppedFile = new File([blob], pendingIconFile.name || 'organization-icon.jpg', { type: mimeType });
      await handleIconUpload(croppedFile);
      setIsCropDialogOpen(false);
      resetCropState();
    } catch (error) {
      addToast({
        title: 'Crop failed',
        description: extractApiErrorMessage(error, 'Unable to crop organization icon.'),
        variant: 'destructive',
      });
    } finally {
      setIsCroppingIcon(false);
    }
  }, [addToast, cropImageSrc, croppedAreaPixels, handleIconUpload, pendingIconFile, resetCropState]);

  const handleCancelIconCrop = useCallback(() => {
    setIsCropDialogOpen(false);
    resetCropState();
  }, [resetCropState]);

  const handleRemoveIcon = useCallback(() => {
    setOrganizationDraft((prev) => ({
      ...prev,
      icon: null,
      icon_media: null,
    }));
  }, []);

  const handleSaveOrganization = useCallback(async () => {
    if (!organizationDraft.name.trim()) {
      addToast({ title: 'Name required', description: 'Enter an organization name.' });
      return;
    }
    if (!organizationDraft.org_id.trim()) {
      addToast({ title: 'Org ID required', description: 'Enter a unique organization ID.' });
      return;
    }

    const allowedEmailDomains = parseDomainList(organizationDraft.allowed_email_domains_text);
    const validationError = getDomainListValidationError(organizationDraft.allowed_email_domains_text);
    if (validationError) {
      organizationDomainValidationActiveRef.current = true;
      setOrganizationDomainError(validationError);
      addToast({
        title: 'Invalid email domain',
        description: validationError,
        variant: 'destructive',
      });
      return;
    }

    setIsSavingOrganization(true);
    const payload = {
      name: organizationDraft.name.trim(),
      org_id: organizationDraft.org_id.trim(),
      icon: organizationDraft.icon,
      is_active: organizationDraft.is_active,
      allowed_email_domains: allowedEmailDomains,
    };

    try {
      if (organizationDraft.id) {
        const response = await adminContent.organizations.update(organizationDraft.id, payload);
        const savedOrganization = response.data as OrganizationSummary;
        setSelectedOrganizationId(savedOrganization.id);
        setOrganizationDraft(organizationToDraft(savedOrganization));
        organizationDomainValidationActiveRef.current = false;
        setOrganizationDomainError(null);
        addToast({ title: 'Organization updated' });
      } else {
        const response = await adminContent.organizations.create(payload);
        const savedOrganization = response.data as OrganizationSummary;
        setSelectedOrganizationId(savedOrganization.id);
        setOrganizationDraft(organizationToDraft(savedOrganization));
        organizationDomainValidationActiveRef.current = false;
        setOrganizationDomainError(null);
        addToast({ title: 'Organization created' });
      }
      await loadOrganizations();
    } catch (error) {
      const domainFieldError = extractApiFieldErrorMessage(error, ['allowed_email_domains']);
      if (domainFieldError) {
        organizationDomainValidationActiveRef.current = true;
        setOrganizationDomainError(domainFieldError);
        return;
      }
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to save organization.'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingOrganization(false);
    }
  }, [addToast, loadOrganizations, organizationDraft]);

  const handleSaveDefaultCourses = useCallback(async () => {
    if (!selectedOrganizationId) return;
    setIsSavingDefaultCourses(true);
    try {
      const response = await adminContent.organizations.updateContentDefaults(selectedOrganizationId, {
        course_ids: draftDefaultCourses.map((assignment) => assignment.course_id),
      });
      applyContentDefaultsResponse(response.data as ContentDefaultsResponse);
      addToast({ title: 'Organization course defaults saved' });
    } catch (error) {
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to save organization course defaults.'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingDefaultCourses(false);
    }
  }, [addToast, applyContentDefaultsResponse, draftDefaultCourses, selectedOrganizationId]);

  const handleSaveDefaultFlows = useCallback(async () => {
    if (!selectedOrganizationId) return;
    setIsSavingDefaultFlows(true);
    try {
      const response = await adminContent.organizations.updateContentDefaults(selectedOrganizationId, {
        flow_ids: draftDefaultFlows.map((assignment) => assignment.flow_id),
      });
      applyContentDefaultsResponse(response.data as ContentDefaultsResponse);
      addToast({ title: 'Organization module defaults saved' });
    } catch (error) {
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to save organization module defaults.'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingDefaultFlows(false);
    }
  }, [addToast, applyContentDefaultsResponse, draftDefaultFlows, selectedOrganizationId]);

  const handleSaveInspireDefaults = useCallback(async () => {
    if (!selectedOrganizationId) return;
    setIsSavingInspireDefaults(true);
    try {
      const response = await adminContent.organizations.updateContentDefaults(selectedOrganizationId, {
        inspire_deck_ids: draftInspireDeckIds,
        override_wisefriend_inspire_defaults: draftOverrideWiseFriendInspireDefaults,
        inspire_program_name: draftInspireProgramName.trim(),
        inspire_program_logo_id: draftInspireProgramLogoId.trim() || null,
      });
      applyContentDefaultsResponse(response.data as ContentDefaultsResponse);
      addToast({ title: 'Inspire program defaults saved' });
    } catch (error) {
      addToast({ title: 'Error', description: extractApiErrorMessage(error, 'Unable to save Inspire defaults.'), variant: 'destructive' });
    } finally {
      setIsSavingInspireDefaults(false);
    }
  }, [addToast, applyContentDefaultsResponse, draftInspireDeckIds, draftInspireProgramLogoId, draftInspireProgramName, draftOverrideWiseFriendInspireDefaults, selectedOrganizationId]);

  const handleSignupCodeDraftChange = useCallback(
    (field: keyof SignupCodeDraft, value: string) => {
      setSignupCodeDraft((prev) => ({
        ...prev,
        [field]: field === 'code' ? normalizeSignupCode(value) : value,
      }));
    },
    [],
  );

  const handleCreateSignupCode = useCallback(async () => {
    if (!selectedOrganizationId) {
      addToast({ title: 'Organization required', description: 'Select an organization first.' });
      return;
    }
    const normalizedCode = normalizeSignupCode(signupCodeDraft.code.trim());
    if (!normalizedCode) {
      addToast({ title: 'Code required', description: 'Enter the signup code users will enter at registration.' });
      return;
    }

    setIsSavingSignupCode(true);
    const payload = {
      name: signupCodeDraft.name.trim(),
      code: normalizedCode,
      organization_id: selectedOrganizationId,
      cohort_id: signupCodeDraft.cohort_id || null,
      role: signupCodeDraft.role,
      expires_at: signupCodeDraft.expires_at
        ? new Date(signupCodeDraft.expires_at).toISOString()
        : undefined,
      is_active: true,
    };

    try {
      await adminContent.signupCodes.create(payload);
      addToast({ title: 'Signup code created' });
      setRevealedSignupCode(normalizedCode);
      setIsRevealDialogOpen(true);
      setSignupCodeDraft(DEFAULT_SIGNUP_CODE_DRAFT());
      await loadSignupCodes(selectedOrganizationId);
    } catch (error) {
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to create signup code.'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingSignupCode(false);
    }
  }, [addToast, loadSignupCodes, selectedOrganizationId, signupCodeDraft]);

  const handleCopyRevealedCode = useCallback(async () => {
    if (!revealedSignupCode) return;
    try {
      await navigator.clipboard.writeText(revealedSignupCode);
      addToast({ title: 'Copied', description: 'Signup code copied to clipboard.' });
    } catch (error) {
      addToast({
        title: 'Copy failed',
        description: 'Could not copy automatically. Please copy the code manually.',
        variant: 'destructive',
      });
    }
  }, [addToast, revealedSignupCode]);

  const handleToggleSignupCode = useCallback(
    async (signupCode: SignupCodeSummary) => {
      try {
        await adminContent.signupCodes.update(signupCode.id, { is_active: !signupCode.is_active });
        if (selectedOrganizationId) {
          await loadSignupCodes(selectedOrganizationId);
        }
      } catch (error) {
        addToast({
          title: 'Error',
          description: extractApiErrorMessage(error, 'Unable to update signup code.'),
          variant: 'destructive',
        });
      }
    },
    [addToast, loadSignupCodes, selectedOrganizationId],
  );

  return (
    <div className="space-y-6">
      <Dialog
        open={isCropDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelIconCrop();
          } else {
            setIsCropDialogOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Crop organization icon</DialogTitle>
            <DialogDescription>Adjust to a square crop for a clean icon appearance.</DialogDescription>
          </DialogHeader>
          <div className="relative h-[360px] rounded-2xl bg-muted overflow-hidden">
            {cropImageSrc ? (
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={(value) => setZoom(value)}
                onCropComplete={onCropComplete}
              />
            ) : null}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Zoom</span>
              <span>{zoom.toFixed(1)}x</span>
            </div>
            <Slider value={zoom} onChange={(value) => setZoom(value)} min={1} max={3} step={0.1} />
          </div>
          <DialogFooter>
            <Button variant="ghost" size="auto" onClick={handleCancelIconCrop} disabled={isCroppingIcon || isUploadingIcon}>
              Cancel
            </Button>
            <Button size="auto" onClick={handleConfirmIconCrop} disabled={isCroppingIcon || isUploadingIcon}>
              {isCroppingIcon || isUploadingIcon ? 'Saving...' : 'Save crop'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="w-full max-w-none" maxWidth="none">
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <CardTitle>Organization Management</CardTitle>
          {selectedOrganizationId ? (
            <Button asChild size="auto" variant="secondary">
              <Link to={`/organization/admin?organization_id=${encodeURIComponent(selectedOrganizationId)}`}>
                Open Organization Dashboard
              </Link>
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)] items-stretch">
          <div className="space-y-3">
            <Button size="auto" variant="secondary" onClick={() => handleSelectOrganization(null)}>
              New Organization
            </Button>
            {isLoadingOrganizations ? (
              <div
                data-testid="organizations-list-loading"
                className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
              >
                <LoadingAnimation size="sm" />
              </div>
            ) : filteredOrganizations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No organizations yet.</p>
            ) : (
              <div className="space-y-2">
                {filteredOrganizations.map((organization) => (
                  <button
                    key={organization.id}
                    type="button"
                    onClick={() => handleSelectOrganization(organization)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-left text-sm transition',
                      selectedOrganizationId === organization.id
                        ? 'border-foundation-orange-500 bg-foundation-orange-50'
                        : 'border-border hover:border-foundation-orange-300',
                    )}
                  >
                    <div className="font-semibold">{organization.name}</div>
                    <div className="text-xs text-muted-foreground">Org ID: {organization.org_id}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={organizationDraft.name}
                onChange={(event) => handleOrganizationDraftChange('name', event.target.value)}
                placeholder="Organization name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-id">Org ID</Label>
              <Input
                id="org-id"
                value={organizationDraft.org_id}
                onChange={(event) => handleOrganizationDraftChange('org_id', event.target.value)}
                placeholder="Unique org identifier"
              />
            </div>
            <div className="space-y-2">
              <Label>Organization Icon</Label>
              <div className="flex flex-wrap items-start gap-3">
                <div className="h-20 w-20 rounded-lg border border-border bg-muted/30 overflow-hidden flex items-center justify-center text-xs text-muted-foreground">
                  {organizationDraft.icon_media?.url ? (
                    <img
                      src={organizationDraft.icon_media.url}
                      alt={`${organizationDraft.name || 'Organization'} icon`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span>No icon</span>
                  )}
                </div>
                <div className="flex flex-col items-start gap-2">
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    className="hidden"
                    onChange={handleIconFileSelected}
                  />
                  <Button
                    type="button"
                    size="auto"
                    variant="secondary"
                    disabled={isUploadingIcon || isCroppingIcon}
                    onClick={() => iconInputRef.current?.click()}
                  >
                    {isUploadingIcon ? 'Uploading...' : 'Upload Icon'}
                  </Button>
                  {organizationDraft.icon ? (
                    <Button
                      type="button"
                      size="auto"
                      variant="ghost"
                      disabled={isUploadingIcon || isCroppingIcon}
                      onClick={handleRemoveIcon}
                    >
                      Remove Icon
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-domains">Allowed Email Domains (Optional)</Label>
              <Textarea
                id="org-domains"
                value={organizationDraft.allowed_email_domains_text}
                onChange={(event) =>
                  handleOrganizationDraftChange('allowed_email_domains_text', event.target.value)
                }
                placeholder="school.edu, students.school.edu"
                aria-invalid={Boolean(organizationDomainError)}
                aria-describedby={organizationDomainError ? 'org-domains-error' : 'org-domains-help'}
              />
              {organizationDomainError ? (
                <p id="org-domains-error" role="alert" className="text-xs text-destructive">
                  {organizationDomainError}
                </p>
              ) : (
                <p id="org-domains-help" className="text-xs text-muted-foreground">
                  Comma-separated DNS domains. A parent domain also allows its subdomains; wildcard entries are not supported.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={organizationDraft.is_active}
                onCheckedChange={(value) => handleOrganizationDraftChange('is_active', Boolean(value))}
              />
              <span className="text-sm">Organization is active</span>
            </div>
            <Button size="auto" onClick={handleSaveOrganization} disabled={isSavingOrganization}>
              {isSavingOrganization ? 'Saving...' : 'Save Organization'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {selectedOrganizationId ? (
        <Card className="w-full max-w-none" maxWidth="none">
          <CardHeader>
            <CardTitle>Signup Codes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 items-stretch">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="code-name">Code Name (Optional)</Label>
                <Input
                  id="code-name"
                  value={signupCodeDraft.name}
                  onChange={(event) => handleSignupCodeDraftChange('name', event.target.value)}
                  placeholder={`Spring intake ${lowerLabel(getInviteeBucketLabel('scholars', signupCodeCohort))}`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code-value">Signup Code</Label>
                <Input
                  id="code-value"
                  value={signupCodeDraft.code}
                  onChange={(event) => handleSignupCodeDraftChange('code', event.target.value)}
                  placeholder="Code users enter on signup"
                  autoComplete="off"
                  className="font-mono uppercase tracking-wide"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code-cohort">Assigned Cohort (Optional)</Label>
                <Select
                  value={signupCodeDraft.cohort_id || 'none'}
                  onValueChange={(value) =>
                    handleSignupCodeDraftChange('cohort_id', value === 'none' ? '' : value)
                  }
                >
                  <SelectTrigger id="code-cohort">
                    <SelectValue placeholder="No cohort assignment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No cohort assignment</SelectItem>
                    {organizationCohorts.map((cohort) => (
                      <SelectItem key={cohort.id} value={cohort.id}>
                        {cohort.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {signupCodeDraft.cohort_id ? (
                <div className="space-y-2">
                  <Label htmlFor="code-role">Assigned Role</Label>
                  <Select
                    value={signupCodeDraft.role}
                    onValueChange={(value) => handleSignupCodeDraftChange('role', value)}
                  >
                    <SelectTrigger id="code-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mentee">{getCohortRoleLabel('mentee', signupCodeCohort)}</SelectItem>
                      <SelectItem value="mentor">{getCohortRoleLabel('mentor', signupCodeCohort)}</SelectItem>
                      <SelectItem value="cohort_director">Cohort Director</SelectItem>
                      <SelectItem value="cohort_admin">Cohort Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="code-expiry">Expires At</Label>
                <Input
                  id="code-expiry"
                  type="datetime-local"
                  value={signupCodeDraft.expires_at}
                  onChange={(event) => handleSignupCodeDraftChange('expires_at', event.target.value)}
                />
              </div>
            </div>
            <Button size="auto" onClick={handleCreateSignupCode} disabled={isSavingSignupCode}>
              {isSavingSignupCode ? 'Creating...' : 'Create Signup Code'}
            </Button>

            <div className="space-y-2">
              <Label>Existing Signup Codes</Label>
              {isLoadingSignupCodes ? (
                <div
                  data-testid="organizations-signup-codes-loading"
                  className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
                >
                  <LoadingAnimation size="sm" />
                </div>
              ) : signupCodes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No signup codes created for this organization.</p>
              ) : (
                <div className="space-y-2">
                  {signupCodes.map((signupCode) => (
                    <div
                      key={signupCode.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                    >
                      <div className="text-sm">
                        <div className="font-semibold">{signupCode.name || 'Unnamed code'}</div>
                        <div className="text-xs text-muted-foreground">
                          Cohort: {signupCode.cohort?.name ?? 'None'} | Role: {signupCode.role}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Expires: {new Date(signupCode.expires_at).toLocaleString()}
                        </div>
                      </div>
                      <Button
                        size="auto"
                        variant="secondary"
                        onClick={() => handleToggleSignupCode(signupCode)}
                      >
                        {signupCode.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {selectedOrganizationId ? (
        <Card className="w-full max-w-none" maxWidth="none">
          <CardHeader>
            <CardTitle>Default Content Assignments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 items-stretch">
            {isLoadingContentDefaults ? (
              <div
                data-testid="organizations-content-defaults-loading"
                className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
              >
                <LoadingAnimation size="sm" />
              </div>
            ) : (
              <>
              <ContentAssignmentEditor
                courseTitle="Default Course Assignments"
                flowTitle="Default Standalone Module Assignments"
                flowDescription="These defaults apply to users in this organization unless their current cohort overrides organization defaults for that content type."
                courseEmptyMessage="No default courses selected for this organization."
                flowEmptyMessage="No default standalone modules selected for this organization."
                courses={draftDefaultCourses}
                flows={draftDefaultFlows}
                onCoursesChange={setDraftDefaultCourses}
                onFlowsChange={setDraftDefaultFlows}
                onSaveCourses={handleSaveDefaultCourses}
                onSaveFlows={handleSaveDefaultFlows}
                coursesDirty={defaultCoursesDirty}
                flowsDirty={defaultFlowsDirty}
                savingCourses={isSavingDefaultCourses}
                savingFlows={isSavingDefaultFlows}
              />
              <div className="space-y-4 border-t border-border pt-6">
                <div>
                  <h3 className="text-base font-semibold">Inspire Program</h3>
                  <p className="text-sm text-muted-foreground">Set the organization-facing name/logo and choose the ordered decks available in meetings.</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div><Label htmlFor="inspire-program-name">Program name</Label><Input id="inspire-program-name" value={draftInspireProgramName} onChange={(event) => setDraftInspireProgramName(event.target.value)} placeholder={organizationDraft.name || 'WiseFriend'} /></div>
                  <div className="space-y-2">
                    <Label>Program logo</Label>
                    <input ref={inspireLogoInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleInspireLogoFileSelected} />
                    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-white p-3">
                      {draftInspireProgramLogoUrl ? <img src={draftInspireProgramLogoUrl} alt="Inspire program logo preview" className="h-12 w-12 rounded-lg object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-[10px] text-muted-foreground">Fallback</div>}
                      <div className="flex flex-1 flex-wrap gap-2">
                        <Button type="button" size="auto" variant="secondary" onClick={() => inspireLogoInputRef.current?.click()} disabled={isUploadingInspireLogo}>{isUploadingInspireLogo ? 'Uploading…' : 'Upload logo'}</Button>
                        {draftInspireProgramLogoId && <Button type="button" size="auto" variant="ghost" onClick={() => { setDraftInspireProgramLogoId(''); setDraftInspireProgramLogoUrl(''); }}>Use organization icon</Button>}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Optional. Falls back to the organization icon, then WiseFriend branding.</p>
                  </div>
                </div>
                <div className="space-y-3 rounded-lg border border-border p-4">
                  <div>
                    <p className="text-sm font-semibold">Deck availability</p>
                    <p className="text-xs text-muted-foreground">Choose where this organization&apos;s meeting deck catalog comes from.</p>
                  </div>
                  <RadioGroup
                    aria-label="Organization Inspire deck source"
                    value={draftOverrideWiseFriendInspireDefaults ? 'organization' : 'wisefriend'}
                    onValueChange={handleInspireCatalogModeChange}
                    className="grid gap-3 md:grid-cols-2"
                  >
                    <Label
                      htmlFor="inspire-catalog-wisefriend"
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                        !draftOverrideWiseFriendInspireDefaults ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-muted/20',
                      )}
                    >
                      <RadioGroupItem id="inspire-catalog-wisefriend" value="wisefriend" className="mt-0.5 shrink-0" />
                      <span>
                        <span className="block text-sm font-semibold">Use WiseFriend defaults</span>
                        <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Automatically includes every published WiseFriend deck, including future additions.</span>
                      </span>
                    </Label>
                    <Label
                      htmlFor="inspire-catalog-organization"
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                        draftOverrideWiseFriendInspireDefaults ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-muted/20',
                      )}
                    >
                      <RadioGroupItem
                        id="inspire-catalog-organization"
                        data-testid="organization-inspire-override-toggle"
                        value="organization"
                        className="mt-0.5 shrink-0"
                      />
                      <span>
                        <span className="block text-sm font-semibold">Choose decks for this organization</span>
                        <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Uses only the decks selected below. Selecting none makes the meeting catalog empty.</span>
                      </span>
                    </Label>
                  </RadioGroup>
                </div>
                {!draftOverrideWiseFriendInspireDefaults && (
                  <p
                    data-testid="organization-inspire-inheritance-summary"
                    className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900"
                  >
                    <span className="font-semibold">{inheritedWiseFriendDecks.length} WiseFriend {inheritedWiseFriendDecks.length === 1 ? 'deck is' : 'decks are'} currently included.</span>{' '}
                    The checked decks below are inherited automatically and do not need to be selected individually.
                  </p>
                )}
                <div
                  data-testid="organization-inspire-deck-selection"
                  className="space-y-2"
                >
                  <Label>{draftOverrideWiseFriendInspireDefaults ? 'Available decks' : 'Included decks'}</Label>
                  {(draftOverrideWiseFriendInspireDefaults ? availableInspireDecks : inheritedWiseFriendDecks).length === 0 ? (
                    <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      {draftOverrideWiseFriendInspireDefaults ? 'No WiseFriend or organization decks are available.' : 'No published WiseFriend decks are currently available.'}
                    </p>
                  ) : (draftOverrideWiseFriendInspireDefaults ? availableInspireDecks : inheritedWiseFriendDecks).map((deck) => (
                    <label key={deck.id} className={cn('flex items-start gap-3 rounded-lg border border-border p-3', draftOverrideWiseFriendInspireDefaults ? 'cursor-pointer bg-white' : 'bg-muted/20')}>
                      <Checkbox
                        checked={!draftOverrideWiseFriendInspireDefaults || draftInspireDeckIds.includes(deck.id)}
                        disabled={!draftOverrideWiseFriendInspireDefaults}
                        onCheckedChange={(checked) => setDraftInspireDeckIds((current) => Boolean(checked) ? [...current, deck.id] : current.filter((id) => id !== deck.id))}
                      />
                      <span>
                        <span className="block text-sm font-medium">{deck.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {deck.owner_organization?.name || 'WiseFriend'} · {draftOverrideWiseFriendInspireDefaults ? deck.status : 'inherited'}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                {draftOverrideWiseFriendInspireDefaults && draftInspireDeckIds.length > 0 && (
                  <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Meeting order</p>
                    {draftInspireDeckIds.map((deckId, index) => {
                      const deck = availableInspireDecks.find((item) => item.id === deckId);
                      if (!deck) return null;
                      return (
                        <div key={deckId} className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-sm font-medium">{index + 1}. {deck.name}</span>
                          <Button type="button" size="icon" variant="ghost" aria-label={`Move ${deck.name} up`} disabled={index === 0} onClick={() => moveDraftInspireDeck(deckId, -1)}><ArrowUp className="h-4 w-4" /></Button>
                          <Button type="button" size="icon" variant="ghost" aria-label={`Move ${deck.name} down`} disabled={index === draftInspireDeckIds.length - 1} onClick={() => moveDraftInspireDeck(deckId, 1)}><ArrowDown className="h-4 w-4" /></Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Button type="button" size="auto" onClick={handleSaveInspireDefaults} disabled={isSavingInspireDefaults}>{isSavingInspireDefaults ? 'Saving…' : 'Save Inspire defaults'}</Button>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {selectedOrganizationId ? (
        <Card className="w-full max-w-none" maxWidth="none">
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
            <CardTitle>Cohorts</CardTitle>
            <Button asChild size="auto" variant="default">
              <Link to="/admin/cohorts?tab=cohorts">Open Cohorts Workspace</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-4 items-stretch">
            <p className="text-sm text-muted-foreground">
              Manage cohort membership, checklist templates, and cohort overrides in the Cohorts workspace.
            </p>
            {organizationCohorts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
                No cohorts are attached to this organization yet.
              </div>
            ) : (
              <div className="space-y-3">
                {organizationCohorts.map((cohort) => (
                  <div
                    key={cohort.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                  >
                    <div className="text-sm">
                      <div className="font-semibold text-foreground">{cohort.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {cohort.start_date || cohort.end_date
                          ? `${cohort.start_date || 'No start date'} to ${cohort.end_date || 'No end date'}`
                          : 'Dates not set'}
                      </div>
                    </div>
                    <Button asChild size="auto" variant="secondary">
                      <Link to={`/admin/cohorts?tab=cohorts&cohortId=${encodeURIComponent(cohort.id)}`}>
                        Open in Cohorts
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
      <Dialog open={isRevealDialogOpen} onOpenChange={setIsRevealDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Signup Code Created</DialogTitle>
            <DialogDescription>
              This code is shown once. Copy and share it securely with users.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm uppercase tracking-wide">
            {revealedSignupCode}
          </div>
          <DialogFooter>
            <Button size="auto" variant="secondary" onClick={() => setIsRevealDialogOpen(false)}>
              Close
            </Button>
            <Button size="auto" onClick={handleCopyRevealedCode}>
              Copy Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;
